import { useContext, useEffect } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router';

import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';

/**
 * Ask before abandoning unsaved work, on both routes out of a page.
 *
 * A surface an operator types into has exactly two exits, and they are stopped by
 * different mechanisms — which is why one component covers both rather than each
 * caller remembering the second:
 *
 *  - **In-app navigation** — a `<Link>`, the side nav, a breadcrumb. The router
 *    owns this and `useBlocker` is the only thing that can hold it. This half
 *    draws the console's own `ConfirmDialog`.
 *  - **Leaving the document** — reload, closing the tab, an address typed over
 *    this one. Only `beforeunload` sees this, and the browser draws its own
 *    dialog with its own words; the string a page supplies has been ignored for
 *    years, which is why none is passed. **No page can replace that dialog**, so
 *    this half stays as it is rather than being dropped — trading a native
 *    prompt for no prompt at all would lose the exit that discards the most.
 *
 * **`dirty` is a function, not a boolean, and that is load-bearing.** The post
 * editor's body lives in the DOM rather than in React state — its own note
 * explains that intercepting every keystroke would break the browser's undo
 * stack, and undo belongs to the writer — so there is no reactive value to
 * watch. Deferring the question to the moment of departure lets the caller read
 * the live surface then, which is both exact and free while nobody is leaving.
 *
 * **This is a component rather than a hook, and that is not a style choice.**
 * `useBlocker` throws outside a data router, and a hook cannot be called
 * conditionally — so the blocking half lives in a child that is mounted only
 * where a data router exists. The app builds one with `createBrowserRouter`, so
 * production always blocks; the test harness renders `<MemoryRouter>`, which is
 * not a data router, and there the guard degrades to `beforeunload` instead of
 * taking every surface that uses it down with a thrown hook.
 */
export function UnsavedGuard({ dirty, subject }: { dirty: () => boolean; subject: ConfirmSubject }) {
  const inDataRouter = useContext(UNSAFE_DataRouterContext) !== null;

  useEffect(() => {
    function onBeforeUnload(event: BeforeUnloadEvent) {
      if (!dirty()) return;
      // `preventDefault` is the modern spelling and `returnValue` is what older
      // engines still read; both are needed for the prompt to appear everywhere.
      event.preventDefault();
      event.returnValue = '';
    }

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  return inDataRouter ? <BlockNavigation dirty={dirty} subject={subject} /> : null;
}

/**
 * The in-app half. Mounted only under a data router — see the note above.
 *
 * **The confirmation is the console's own dialog, never `window.confirm`.** An
 * earlier spelling reached for the native prompt on the belief that «a blocker
 * needs a synchronous answer»; it does not. `useBlocker` holds the navigation
 * open until `proceed()` or `reset()` is called, with no deadline — which is
 * exactly what lets an asynchronous dialog answer it, and is the pattern
 * `features/content/new` already used against this same hook. The native prompt
 * is unstyleable, labels its two buttons in the browser's language rather than
 * the surface's, sits outside the RTL page, and is invisible to this console's
 * own tests.
 */
function BlockNavigation({ dirty, subject }: { dirty: () => boolean; subject: ConfirmSubject }) {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname === nextLocation.pathname) return false;
    return dirty();
  });

  const blocked = blocker.state === 'blocked';

  return (
    <ConfirmDialog
      open={blocked}
      subject={blocked ? subject : null}
      onConfirm={() => blocker.proceed?.()}
      onCancel={() => blocker.reset?.()}
    />
  );
}
